import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Checkbox, Drawer, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import BillingLinesEditor, { billingMonthly, BillingLineState } from '../../../../components/Billing/BillingLinesEditor';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericInfoModal from '../../../../components/GenericInfoModal';
import SegmentedControl from '../../../../components/SegmentedControl';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { ActionMode, GeneralStatusEnum } from '../../../../common/enums/common.enum';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { BillingItemDto, BillingSchemeDto } from '../../../../common/interfaces/invoicing.interface';
import {
  assignSchemeToGroup,
  createBillingScheme,
  getActiveBillingItems,
  getBillingSchemes,
  updateBillingScheme
} from '../../../../api/invoicing.api';
import { getGroupsWithLocationName } from '../../../../api/kindergarten.api';
import { palette } from '../../../../theme';
import { FormTextField } from '../Form/FormField';
import ErrorSummary, { SummaryItem } from '../Form/ErrorSummary';
import { readServerErrors } from '../Form/serverErrors';
import { collectErrors } from '../../validation/rules';
import { billingSchemeSchema } from '../../validation/billingScheme.schema';

interface SchemeDrawerProps {
  mode: ActionMode;
  kindergartenId: string;
  vatApplicable: boolean;
  scheme?: BillingSchemeDto | null;
  /** A new price list started from a group's page comes with that group ticked. */
  presetGroupIds?: string[];
  onClose: () => void;
  onSaved: () => void;
}

type GroupNote = { text: string; warning?: boolean };

/** "Leptirići - Zemun" as the groups list names it, shown as "Leptirići · Zemun". */
const groupLabel = (name: string) => name.replace(/ - (?!.* - )/, ' · ');

const snapshot = (name: string, status: GeneralStatusEnum, lines: BillingLineState[], groupIds: string[]) =>
  JSON.stringify([
    name.trim(),
    status,
    lines.map(line => [line.billingItemId, line.quantity ?? 1, line.unitPriceNetOverride, line.vatRateOverride, line.pdfLabelOverride]),
    [...groupIds].sort()
  ]);

/**
 * A group price list, in the same side panel as a child's contract: what it is called,
 * which groups pay by it, and what it charges.
 *
 * The groups are a list with what choosing each one does - a group has one price list, so
 * ticking a group that has another moves it here, and the note says so before saving rather
 * than after. Discounts are not offered here: a discount belongs to one child.
 */
const SchemeDrawer: React.FC<SchemeDrawerProps> = ({ mode, kindergartenId, vatApplicable, scheme, presetGroupIds, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [name, setName] = useState(scheme?.name ?? '');
  const [status, setStatus] = useState<GeneralStatusEnum>(scheme?.status ?? GeneralStatusEnum.ACTIVE);
  const [lines, setLines] = useState<BillingLineState[]>([]);
  const [initialLines, setInitialLines] = useState<BillingLineState[]>([]);
  const [catalog, setCatalog] = useState<BillingItemDto[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  /** The price list each group is on now, other than this one. */
  const [groupSchemes, setGroupSchemes] = useState<Record<string, { id: string; name: string }>>({});
  const initialGroupIds = useMemo(() => scheme?.groups.map(group => group.id) ?? [], [scheme]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(scheme ? initialGroupIds : (presetGroupIds ?? []));
  const [touched, setTouched] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [serverOther, setServerOther] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  /**
   * What the server already holds from an earlier attempt: the list a create made, and the
   * groups it carries. Saving is a create plus one call per group, so a group that fails
   * after the create must not make the retry create a second list with the same name.
   */
  const createdId = useRef<string | null>(null);
  const assignedGroupIds = useRef<string[] | null>(null);

  const loadCatalog = useCallback(async () => {
    try {
      const result = await getActiveBillingItems(kindergartenId);
      setCatalog(result.results ?? []);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.schemes.catalogueLoadFailed'));
    }
  }, [kindergartenId, t]);

  useEffect(() => {
    loadCatalog();
    getGroupsWithLocationName(kindergartenId)
      .then(result => setGroups((result.results ?? []).map(group => ({ id: group.id, name: group.name }))))
      .catch(() => undefined);
    // which list each group is on comes from the lists themselves: no group call says it
    getBillingSchemes(kindergartenId)
      .then(result => {
        const byGroup: Record<string, { id: string; name: string }> = {};
        (result.results ?? []).forEach(other => other.groups.forEach(group => (byGroup[group.id] = { id: other.id, name: other.name })));
        setGroupSchemes(byGroup);
      })
      .catch(() => undefined);
  }, [loadCatalog, kindergartenId]);

  // seed the lines once the catalogue is known, so each row can show what it inherits
  useEffect(() => {
    if (!scheme || !catalog.length) return;
    const seeded = scheme.items.map(item => {
      const fromCatalog = catalog.find(entry => entry.id === item.billingItemId);
      return {
        billingItemId: item.billingItemId,
        quantity: item.quantity,
        unitPriceNetOverride: item.overriddenFields.includes('unitPriceNet') ? item.unitPriceNet : null,
        vatRateOverride: item.overriddenFields.includes('vatRate') ? item.vatRate : null,
        pdfLabelOverride: item.overriddenFields.includes('pdfLabel') ? item.pdfLabel : null,
        sortOrder: item.sortOrder,
        name: item.name,
        type: item.type,
        catalogPrice: fromCatalog?.unitPriceNet ?? item.unitPriceNet,
        catalogVat: fromCatalog?.vatRate ?? item.vatRate,
        catalogLabel: fromCatalog?.pdfLabel ?? item.pdfLabel,
        isInactive: item.status !== GeneralStatusEnum.ACTIVE
      };
    });
    setLines(seeded);
    setInitialLines(seeded);
  }, [scheme, catalog]);

  const schema = useMemo(() => billingSchemeSchema(t), [t]);
  // the description is no longer edited here; whatever it holds is saved back as it is
  const description = scheme?.description ?? '';
  const errors = collectErrors(schema, { name, description, items: lines });
  const lineErrors: Record<string, string> = {};
  lines.forEach((line, index) => {
    const message = errors[`items[${index}].quantity`] ?? errors[`items[${index}].unitPriceNetOverride`];
    if (message && attempted) lineErrors[line.billingItemId] = message;
  });
  const nameError = touched || attempted ? errors.name : undefined;

  const dirty =
    snapshot(name, status, lines, selectedGroupIds) !==
    snapshot(scheme?.name ?? '', scheme?.status ?? GeneralStatusEnum.ACTIVE, initialLines, initialGroupIds);

  const close = () => (dirty && !saving ? setConfirmClose(true) : onClose());

  const noteFor = (groupId: string): GroupNote => {
    const selected = selectedGroupIds.includes(groupId);
    const wasHere = initialGroupIds.includes(groupId);
    const other = groupSchemes[groupId] && groupSchemes[groupId].id !== scheme?.id ? groupSchemes[groupId] : null;
    if (selected && wasHere) return { text: t('billing.schemes.groups.onThis') };
    if (!selected && wasHere) return { text: t('billing.schemes.groups.leaves'), warning: true };
    if (selected && other) return { text: t('billing.schemes.groups.movesFrom', { name: other.name }), warning: true };
    if (!selected && other) return { text: t('billing.schemes.groups.now', { name: other.name }) };
    if (!selected) return { text: t('billing.schemes.groups.none'), warning: true };
    return { text: t('billing.schemes.groups.gets') };
  };

  const handleSave = async (): Promise<void> => {
    setAttempted(true);
    setServerOther([]);
    if (Object.keys(errors).length) {
      if (errors.name) document.getElementById('scheme-name')?.focus();
      return;
    }
    const dto = {
      name: name.trim(),
      description: description.trim() || undefined,
      status,
      items: lines.map((line, index) => ({
        billingItemId: line.billingItemId,
        quantity: line.quantity ?? 1,
        unitPriceNetOverride: line.unitPriceNetOverride,
        vatRateOverride: line.vatRateOverride,
        pdfLabelOverride: line.pdfLabelOverride,
        sortOrder: index
      }))
    };
    setSaving(true);
    let schemeSaved = false;
    try {
      const existingId = scheme?.id ?? createdId.current;
      const saved = existingId
        ? await updateBillingScheme(kindergartenId, existingId, dto)
        : await createBillingScheme(kindergartenId, dto);
      const schemeId = saved.results?.id ?? existingId;
      schemeSaved = true;
      if (!existingId && schemeId) createdId.current = schemeId;
      // assignment is per group on the backend, so only what changed is sent - measured
      // against what is already assigned, which after a failed attempt is not the start
      if (schemeId) {
        const assigned = assignedGroupIds.current ?? initialGroupIds;
        for (const groupId of selectedGroupIds.filter(id => !assigned.includes(id))) {
          await assignSchemeToGroup(kindergartenId, groupId, schemeId);
          assignedGroupIds.current = [...(assignedGroupIds.current ?? initialGroupIds), groupId];
        }
        for (const groupId of assigned.filter(id => !selectedGroupIds.includes(id))) {
          await assignSchemeToGroup(kindergartenId, groupId, null);
          assignedGroupIds.current = (assignedGroupIds.current ?? initialGroupIds).filter(id => id !== groupId);
        }
      }
      createNotification(MESSAGE_TYPE.SUCCESS, t('billing.schemes.saved'));
      onSaved();
      onClose();
    } catch (error: any) {
      const read = readServerErrors(error, ['name', 'description', 'items']);
      if (!read) createNotification(MESSAGE_TYPE.ERROR, t('billing.schemes.saveFailed'));
      else setServerOther([...Object.values(read.fields), ...read.other]);
      // the list itself was saved and only a group failed: the screen behind shows it now,
      // while the panel stays open for the retry
      if (schemeSaved) onSaved();
    } finally {
      setSaving(false);
    }
  };

  const summary: SummaryItem[] = attempted
    ? [
        ...(errors.name ? [{ id: 'scheme-name', label: t('billing.schemes.name'), message: errors.name }] : []),
        ...(errors.items ? [{ message: errors.items }] : []),
        ...lines
          .filter(line => lineErrors[line.billingItemId])
          .map(line => ({ message: `${line.name}: ${lineErrors[line.billingItemId]}` })),
        ...serverOther.map(message => ({ message }))
      ]
    : [];

  const selectedNames = groups.filter(group => selectedGroupIds.includes(group.id)).map(group => groupLabel(group.name));
  const monthly = billingMonthly(lines, [], false);

  return (
    <Drawer
      anchor="right"
      open
      onClose={close}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-modal': true,
          'aria-label': name.trim() || t('billing.schemes.newTitle'),
          // the theme paints every Drawer as the purple navigation sidebar; this one is a panel
          sx: {
            width: 720,
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'surface.main',
            color: 'text.primary',
            borderRight: 'none'
          }
        } as object,
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,.5)' } }
      }}
    >
      <Box sx={{ p: '24px 24px 16px', borderBottom: 1, borderColor: 'line' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('billing.schemes.eyebrow')}</Typography>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
              {mode === ActionMode.CREATE && !scheme ? t('billing.schemes.newTitle') : scheme?.name || name}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              {selectedNames.length
                ? t('billing.schemes.appliesTo', { count: selectedNames.length, names: selectedNames.join(', ') })
                : t('billing.schemes.appliesToNone')}
            </Typography>
          </Box>
          <IconButton aria-label={t('common.close')} onClick={close} sx={{ mt: -0.5, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 3, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
        <ErrorSummary items={summary} radius="10px" />

        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: 2, alignItems: 'start' }}>
          <FormTextField
            id="scheme-name"
            label={t('billing.schemes.name')}
            required
            value={name}
            onChange={event => setName(event.target.value)}
            onBlur={() => setTouched(true)}
            error={nameError}
            hint={t('billing.schemes.nameHint')}
          />
          <Box>
            <Typography id="scheme-status-label" sx={{ fontSize: 13, fontWeight: 500, mb: 0.75 }}>
              {t('common.status')}
            </Typography>
            <SegmentedControl
              labelledBy="scheme-status-label"
              value={status}
              onChange={setStatus}
              options={[
                { value: GeneralStatusEnum.ACTIVE, label: t('billing.schemes.active') },
                { value: GeneralStatusEnum.INACTIVE, label: t('billing.schemes.inactive') }
              ]}
            />
          </Box>
        </Box>

        <Box component="section" aria-labelledby="scheme-groups-title">
          <Typography id="scheme-groups-title" component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
            {t('billing.schemes.groups.title')}
          </Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>{t('billing.schemes.groups.description')}</Typography>
          {!groups.length ? (
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('billing.schemes.groups.empty')}</Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, border: `1px solid ${palette.line}`, borderRadius: '10px' }}>
              {groups.map(group => {
                const checked = selectedGroupIds.includes(group.id);
                const note = noteFor(group.id);
                const id = `scheme-group-${group.id}`;
                return (
                  <Box
                    component="li"
                    key={group.id}
                    sx={{
                      minHeight: 52,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      '& + &': { borderTop: `1px solid ${palette.line}` }
                    }}
                  >
                    <Checkbox
                      id={id}
                      checked={checked}
                      onChange={event =>
                        setSelectedGroupIds(prev => (event.target.checked ? [...prev, group.id] : prev.filter(other => other !== group.id)))
                      }
                      slotProps={{ input: { 'aria-describedby': `${id}-note` } }}
                    />
                    <Typography component="label" htmlFor={id} sx={{ fontSize: 14, flex: 1, cursor: 'pointer' }}>
                      {groupLabel(group.name)}
                    </Typography>
                    <Typography
                      id={`${id}-note`}
                      sx={{ fontSize: 12, color: note.warning ? 'warning.main' : 'text.secondary', pr: 1, textAlign: 'right' }}
                    >
                      {note.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box component="section" aria-labelledby="scheme-items-title">
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography id="scheme-items-title" component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
              {t('billing.schemes.items.title')}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('billing.schemes.items.pricesNote')}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>{t('billing.schemes.items.description')}</Typography>
          <BillingLinesEditor
            lines={lines}
            onChange={setLines}
            catalog={catalog}
            vatApplicable={vatApplicable}
            inactiveNotice={t('billing.schemes.inactiveNotice')}
            lineErrors={lineErrors}
            listError={attempted ? errors.items : undefined}
            showTotal={false}
          />
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ p: '16px 24px', borderTop: 1, borderColor: 'line' }}
      >
        <Box>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('billing.schemes.footer.monthly')}</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} data-testid="scheme-monthly">
            {formatMoney(monthly.net, { withCurrency: true })}
          </Typography>
          {monthly.perDay.length > 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('billing.schemes.footer.perDay', { names: monthly.perDay.join(', ') })}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button onClick={close} sx={{ textTransform: 'none', fontSize: 16, color: 'text.primary', px: 2, borderRadius: '10px' }}>
            {t('common.cancel')}
          </Button>
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            label={t('billing.schemes.save')}
            onAsyncAction={handleSave}
            disabled={saving}
          />
        </Stack>
      </Stack>

      <GenericInfoModal
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          onClose();
        }}
        label={t('billing.settings.unsaved.leaveTitle')}
        message={t('billing.schemes.unsavedMessage')}
        confirmLabel={t('billing.settings.unsaved.leaveConfirm')}
        closeLabel={t('billing.settings.unsaved.stay')}
      />
    </Drawer>
  );
};

export default SchemeDrawer;
